import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../lib/auth';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();

  if (isLoaded && isSignedIn) {
    return <Navigate to="/app" />;
  }

  const getStartedTo = '/login';

  return (
    <div className="min-h-screen bg-snow">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-snow/80 backdrop-blur-md border-b border-mist">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🍽️</span>
              <span className="font-display text-xl font-semibold text-ink">MiseBook</span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate({ to: getStartedTo })}
                className="btn btn-primary"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral-50 text-coral-600 text-sm font-medium mb-6">
            ✨ Meal planning made simple
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-ink leading-tight mb-6">
            Finally agree on{' '}
            <span className="text-coral-500">dinner</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-stone max-w-2xl mx-auto mb-10 leading-relaxed">
            MiseBook is the meal planning app for couples who love cooking together. 
            Import your favorite recipes, plan your week, and generate grocery lists — all in one beautiful place.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => navigate({ to: getStartedTo })}
              className="btn btn-primary btn-lg"
            >
              Get Started Free
            </button>
            <a href="#features" className="btn btn-ghost btn-lg">
              See How It Works
            </a>
          </div>
          
          <p className="mt-6 text-sm text-stone">
            Free forever. Invite your partner to cook together.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-sand/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink mb-4">
              Everything you need to plan meals together
            </h2>
            <p className="text-stone text-lg max-w-2xl mx-auto">
              No more "what's for dinner?" debates. MiseBook makes meal planning 
              something you'll actually enjoy doing together.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              emoji="📖"
              title="Import Any Recipe"
              description="Paste a link from any recipe site and we'll automatically import it. Your favorite dishes, beautifully organized."
            />
            <FeatureCard
              emoji="📅"
              title="Plan Your Week"
              description="Drag and drop recipes onto your calendar. See the whole week at a glance, or focus on one day at a time."
            />
            <FeatureCard
              emoji="🛒"
              title="Smart Grocery Lists"
              description="Auto-generate shopping lists from your meal plan. Check items off as you shop — together or apart."
            />
            <FeatureCard
              emoji="👥"
              title="Cook Together"
              description="Share your kitchen with your partner. Both of you can add recipes, plan meals, and check off groceries."
            />
            <FeatureCard
              emoji="📱"
              title="Works Everywhere"
              description="Beautiful on desktop, perfect on mobile. Take your grocery list to the store or browse recipes on the couch."
            />
            <FeatureCard
              emoji="✨"
              title="Delightfully Simple"
              description="No bloat, no subscriptions, no ads. Just a beautiful app that helps you eat better, together."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink mb-4">
              How it works
            </h2>
          </div>
          
          <div className="space-y-12">
            <Step
              number={1}
              title="Import your favorite recipes"
              description="Copy any recipe URL and paste it into MiseBook. We'll extract the ingredients, instructions, and photos automatically."
            />
            <Step
              number={2}
              title="Plan your week together"
              description="Drag recipes onto your calendar to plan breakfast, lunch, and dinner. Your partner sees the same plan in real-time."
            />
            <Step
              number={3}
              title="Shop with confidence"
              description="Generate a grocery list from your meal plan. Items are organized by category so you can breeze through the store."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-coral-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink mb-4">
            Ready to simplify dinner?
          </h2>
          <p className="text-stone text-lg mb-8">
            Start planning meals together in seconds. No credit card required.
          </p>
          <button 
            onClick={() => navigate({ to: getStartedTo })}
            className="btn btn-primary btn-lg"
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-mist">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🍽️</span>
            <span className="font-display font-semibold text-ink">MiseBook</span>
          </div>
          <p className="text-stone text-sm">
            Made with 🧡 for couples who cook together
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ emoji, title, description }: { emoji: string; title: string; description: string }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-mist hover:border-coral-200 transition-colors">
      <div className="w-12 h-12 rounded-xl bg-coral-50 flex items-center justify-center text-2xl mb-4">
        {emoji}
      </div>
      <h3 className="font-display font-semibold text-ink text-lg mb-2">{title}</h3>
      <p className="text-stone">{description}</p>
    </div>
  );
}

function Step({ number, title, description }: { number: number; title: string; description: string }) {
  return (
    <div className="flex gap-6">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-coral-500 text-white flex items-center justify-center font-display font-semibold text-lg">
        {number}
      </div>
      <div>
        <h3 className="font-display font-semibold text-ink text-xl mb-2">{title}</h3>
        <p className="text-stone text-lg">{description}</p>
      </div>
    </div>
  );
}
